import type { FrontendDataType } from '@/lib/field-types'
import React from 'react'
import { z } from 'zod'

// =============================================================================
// SHARED UTILITIES (similar to NodeDataDisplay but simpler)
// =============================================================================

// Get container styling - only box the outermost level
const getContainerClasses = (depth: number): string => {
  if (depth === 0) {
    return 'rounded-lg border border-gray-200 bg-gray-50 p-3'
  }
  return 'relative py-1' // Tighter spacing for schema structure
}

// Get padding for nested items
const getNestedPadding = (depth: number): React.CSSProperties => ({
  paddingLeft: `${depth * 12 + 8}px`, // Slightly less padding than NodeDataDisplay
})

// Create left border bars for nested levels
const getLeftBorderIndicator = (depth: number): React.ReactNode => {
  if (depth === 0) return null

  return (
    <div className="absolute bottom-0 left-0 top-0 flex">
      <div
        className="mr-3 mt-1 w-0.5 bg-gray-300"
        style={{
          height: 'calc(100% - 4px)',
          opacity: 0.7,
        }}
      />
    </div>
  )
}

// =============================================================================
// TYPE BADGE COMPONENTS (reused from NodeDataDisplay)
// =============================================================================

// Get color classes for different types
const getTypeColor = (type: FrontendDataType | string): string => {
  switch (type) {
    case 'string':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'number':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'boolean':
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'date':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'object':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    case 'enum':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'discriminated-union':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200'
    case 'union':
      return 'bg-pink-100 text-pink-800 border-pink-200'
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200'
  }
}

// Individual type badge component
const TypeBadge: React.FC<{ type: FrontendDataType | string }> = ({ type }) => {
  const colorClasses = getTypeColor(type)
  return (
    <span className={`rounded border px-2 py-0.5 text-xs font-medium ${colorClasses}`}>{type}</span>
  )
}

// Array badge component
const ArrayBadge: React.FC = () => (
  <span className="rounded border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
    array
  </span>
)

// Optional badge component
const OptionalBadge: React.FC = () => (
  <span className="rounded border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
    optional
  </span>
)

// Type badges container - shows multiple independent badges
interface TypeBadgesProps {
  type: FrontendDataType | string
  isArray?: boolean
  isOptional?: boolean
}

const TypeBadges: React.FC<TypeBadgesProps> = ({ type, isArray, isOptional }) => (
  <div className="flex flex-wrap items-center gap-1">
    <TypeBadge type={type} />
    {isArray && <ArrayBadge />}
    {isOptional && <OptionalBadge />}
  </div>
)

// =============================================================================
// SCHEMA STRUCTURE TYPES
// =============================================================================

// Schema field type for structure visualization
interface SchemaFieldInfo {
  name: string
  type: FrontendDataType | string
  isOptional: boolean
  isArray: boolean
  description?: string
  properties?: SchemaFieldInfo[]
  enumValues?: string[]
  arrayElementType?: FrontendDataType | string
}

// =============================================================================
// ZOD SCHEMA CONVERSION
// =============================================================================

// Convert Zod schema to schema field info (structure only)
const convertZodSchemaToStructure = (
  schema: z.ZodType<any>,
  name: string = 'root'
): SchemaFieldInfo => {
  const typeName = (schema._def as any).typeName as string

  // Handle nullable schemas (ignore nullable as a tag)
  if (typeName === 'ZodNullable') {
    return convertZodSchemaToStructure((schema._def as any).innerType, name)
  }

  // Handle optional schemas
  if (typeName === 'ZodOptional') {
    const inner = convertZodSchemaToStructure((schema._def as any).innerType, name)
    return { ...inner, isOptional: true }
  }

  // Handle array schemas
  if (typeName === 'ZodArray') {
    const element = convertZodSchemaToStructure((schema._def as any).type, 'element')

    return {
      name,
      type: element.type, // Use the element type as the main type (string, object, etc.)
      isOptional: false,
      isArray: true,
      arrayElementType: element.type,
      // If the array's element is itself an array, render that nested array as a single child
      // If the array element is an object, show its properties (including any nested arrays)
      // For unions/discriminated unions, show their properties as children
      properties: (element as any).isArray
        ? [element]
        : element.type === 'object' ||
            element.type === 'union' ||
            element.type === 'discriminated-union'
          ? element.properties
          : undefined,
    }
  }

  // Handle object schemas
  if (typeName === 'ZodObject') {
    const shape = (schema._def as any).shape()
    const properties: SchemaFieldInfo[] = Object.entries(shape).map(([key, fieldSchema]) =>
      convertZodSchemaToStructure(fieldSchema as z.ZodType<any>, key)
    )

    return {
      name,
      type: 'object',
      isOptional: false,
      isArray: false,
      properties,
    }
  }

  // Handle union schemas (discriminated unions)
  if (typeName === 'ZodDiscriminatedUnion') {
    // Extract all possible variants
    const discriminatorKey = (schema._def as any).discriminator
    const variants: SchemaFieldInfo[] = []

    // Get all options from the union (options is an array, not an object)
    if ((schema._def as any).options && Array.isArray((schema._def as any).options)) {
      ;(schema._def as any).options.forEach((variantSchema: z.ZodType<any>) => {
        // Get the discriminator value from the variant schema
        const discriminatorField = (variantSchema as any)?.shape?.[discriminatorKey]
        let discriminatorValue = 'unknown'

        if (discriminatorField && discriminatorField._def?.value !== undefined) {
          discriminatorValue = String(discriminatorField._def.value)
        }

        const variantField = convertZodSchemaToStructure(
          variantSchema,
          `IF ${discriminatorKey} IS ${discriminatorValue}`
        )
        variants.push(variantField)
      })
    }

    return {
      name,
      type: 'discriminated-union',
      isOptional: false,
      isArray: false,
      properties: variants,
    }
  }

  // Handle regular union schemas
  if (typeName === 'ZodUnion') {
    const variants: SchemaFieldInfo[] = []

    // Get all options from the union (options is an array)
    if ((schema._def as any).options && Array.isArray((schema._def as any).options)) {
      ;(schema._def as any).options.forEach((optionSchema: z.ZodType<any>, index: number) => {
        // Get the type name for this option to create a descriptive name
        const variantName = index === 0 ? 'EITHER' : 'OR'
        const variantField = convertZodSchemaToStructure(optionSchema, variantName)
        variants.push(variantField)
      })
    }

    return {
      name,
      type: 'union',
      isOptional: false,
      isArray: false,
      properties: variants,
    }
  }

  // Handle enum schemas
  if (typeName === 'ZodEnum') {
    return {
      name,
      type: 'enum',
      isOptional: false,
      isArray: false,
      enumValues: (schema._def as any).values,
    }
  }

  // Handle primitive types
  let type: FrontendDataType | string

  switch (typeName) {
    case 'ZodString':
      type = 'string'
      break
    case 'ZodNumber':
      type = 'number'
      break
    case 'ZodBoolean':
      type = 'boolean'
      break
    case 'ZodDate':
      type = 'date'
      break
    case 'ZodAny':
      type = 'any'
      break
    case 'ZodUnknown':
      type = 'unknown'
      break
    default:
      type = typeName?.replace('Zod', '').toLowerCase() || 'unknown'
  }

  return {
    name,
    type,
    isOptional: false,
    isArray: false,
  }
}

// =============================================================================
// SCHEMA FIELD RENDERER
// =============================================================================

// Schema field renderer component (structure only, no values, no collapsing)
interface SchemaFieldRendererProps {
  field: SchemaFieldInfo
  depth?: number
}

const SchemaFieldRenderer: React.FC<SchemaFieldRendererProps> = ({ field, depth = 0 }) => {
  const containerClasses = getContainerClasses(depth)
  const paddingStyle = getNestedPadding(depth)

  return (
    <div className={containerClasses} style={paddingStyle}>
      {getLeftBorderIndicator(depth)}
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">{field.name}</span>
        <TypeBadges type={field.type} isArray={field.isArray} isOptional={field.isOptional} />

        {/* Additional info badges */}
        {field.properties && (
          <span className="text-xs text-gray-500">({field.properties.length} fields)</span>
        )}
        {field.enumValues && (
          <span className="text-xs text-gray-500">({field.enumValues.length} options)</span>
        )}
      </div>

      {/* Always show nested properties (no collapsing) */}
      {field.properties && field.properties.length > 0 && (
        <div className="mt-1 space-y-1">
          {field.properties.map((prop, index) => (
            <SchemaFieldRenderer key={`${prop.name}-${index}`} field={prop} depth={depth + 1} />
          ))}
        </div>
      )}

      {/* Show enum values in a compact way */}
      {field.enumValues && (
        <div className="mt-1 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs">
          <div className="mb-1 font-medium text-yellow-800">Options:</div>
          <div className="flex flex-wrap gap-1">
            {field.enumValues.map((value) => (
              <span key={value} className="rounded bg-yellow-100 px-1 py-0.5 text-yellow-700">
                {value}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

// Output schema section component
interface OutputSchemaDisplayProps {
  title: string
  schema: z.ZodType<any>
}

export const OutputSchemaDisplay: React.FC<OutputSchemaDisplayProps> = ({ title, schema }) => {
  const schemaStructure = convertZodSchemaToStructure(schema, 'output')

  return (
    <div>
      <h4 className="mb-3 text-sm font-medium text-gray-900">
        {title}
        <span className="ml-2 text-xs font-normal text-gray-500">(schema)</span>
        {/* Show array indicator in title for top-level arrays */}
        {schemaStructure.isArray && (
          <span className="ml-2 rounded border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
            array
          </span>
        )}
      </h4>
      <div className="space-y-2">
        {schemaStructure.properties ? (
          schemaStructure.properties.map((field) => (
            <SchemaFieldRenderer key={field.name} field={field} />
          ))
        ) : (
          <SchemaFieldRenderer field={schemaStructure} />
        )}
      </div>
    </div>
  )
}
